import React from "react"
import { twMerge } from "tailwind-merge"

import { BASE_INPUT_CLASSNAMES } from "../../../../components/shared/Inputs"
import { DEFAULT_LOGO_FONT, type LogoFontName } from "../constants/logoFonts"

import { FontSelectWithPreview } from "./FontSelectWithPreview"
import { TextLogoView } from "./TextLogoView"

export type TextLogoValue = {
  mainLine: string
  secondLine: string
  mainFont: LogoFontName
  secondFont: LogoFontName
}

type TextLogoEditorProps = {
  value: TextLogoValue
  onChange: (value: TextLogoValue) => void
  previewColor?: string
  previewBackgroundColor?: string
  className?: string
  /** Render-time props mirrored from LogoForm so the live preview reflects sliders. */
  previewMainFontSize?: number
  previewSecondFontSize?: number
  previewMainLetterSpacing?: number
  previewSecondLetterSpacing?: number
  previewLineSpacing?: number
  previewAlignment?: "left" | "center"
}

const defaultTextLogo: TextLogoValue = {
  mainLine: "",
  secondLine: "",
  mainFont: DEFAULT_LOGO_FONT,
  secondFont: DEFAULT_LOGO_FONT
}

export function getDefaultTextLogo(practiceName?: string): TextLogoValue {
  return {
    ...defaultTextLogo,
    mainLine: practiceName || ""
  }
}

export function TextLogoEditor({
  value,
  onChange,
  previewColor = "#FFFFFF",
  previewBackgroundColor = "#374151",
  className,
  previewMainFontSize,
  previewSecondFontSize,
  previewMainLetterSpacing,
  previewSecondLetterSpacing,
  previewLineSpacing,
  previewAlignment
}: TextLogoEditorProps): React.ReactElement {
  const v = { ...defaultTextLogo, ...value }
  const mainLine = v.mainLine || "Practice Name"
  const secondLine = v.secondLine || ""

  const update = (patch: Partial<TextLogoValue>) => {
    onChange({ ...v, ...patch })
  }

  const previewJustifyClass = previewAlignment === "center" ? "items-center" : "items-start"

  return (
    <div className={twMerge("space-y-4", className)}>
      <div>
        <label className="mb-1 block text-sm font-bold text-gray-dark">Main line</label>
        <input
          type="text"
          value={v.mainLine}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => update({ mainLine: e.target.value })}
          placeholder="Practice name or main title"
          className={BASE_INPUT_CLASSNAMES}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-bold text-gray-dark">Second line (optional)</label>
        <input
          type="text"
          value={v.secondLine}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => update({ secondLine: e.target.value })}
          placeholder="e.g. MASSAGE, WELLNESS"
          className={BASE_INPUT_CLASSNAMES}
        />
      </div>
      <FontSelectWithPreview
        label="Font for main line"
        value={v.mainFont}
        onChange={(mainFont) => update({ mainFont })}
      />
      <FontSelectWithPreview
        label="Font for second line"
        value={v.secondFont}
        onChange={(secondFont) => update({ secondFont })}
      />

      {/* Live preview — uses TextLogoView so it stays 1:1 with the rendered site. */}
      <div>
        <span className="mb-2 block text-sm font-bold text-gray-dark">Preview</span>
        <div
          className={`flex ${previewJustifyClass} justify-center rounded-lg p-4`}
          style={{ backgroundColor: previewBackgroundColor }}>
          <TextLogoView
            mainLine={mainLine}
            secondLine={secondLine || undefined}
            mainFont={v.mainFont}
            secondFont={v.secondFont}
            color={previewColor}
            mainFontSize={previewMainFontSize != null ? `${previewMainFontSize}px` : "24px"}
            secondFontSize={previewSecondFontSize != null ? `${previewSecondFontSize}px` : undefined}
            mainLetterSpacing={previewMainLetterSpacing}
            secondLetterSpacing={previewSecondLetterSpacing}
            lineSpacing={previewLineSpacing}
            alignment={previewAlignment}
          />
        </div>
      </div>
    </div>
  )
}
